import React from 'react';
import PropTypes from 'prop-types';

import {Autocomplete, TextField, CircularProgress} from '@mui/material';

import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import backend from 'utils/backend';

const CaregiverAutocomplete = (props) => {
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fetchNewData, setFetchNewData] = React.useState(true);

  const {selectedCaregiver, setSelectedCaregiver, label} = props;

  React.useEffect(() => {
    if (selectedCaregiver) {
      setInputValue(selectedCaregiver.caregiverName);
    }
  }, []);

  React.useEffect(() => {
    if (loading) {
      return;
    } else if (fetchNewData ) {
      const fetchData = async () => {
        const localMatches = options.filter((option) =>
          option.caregiverName.toLowerCase().includes(inputValue.toLowerCase()),
        );

        // If less than 3 local matches, fetch new data
        if (localMatches.length < 3) {
          setLoading(true);

          try {
            const filter = [];
            if (inputValue) {
              filter.push({key: 'caregiverName', value: `${inputValue.trim()}*`});
            }
            filter.push({key: 'status', value: `ACTIVE`});
            const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};

            const result = await backend.caregiver.queryCaregiver(filter, sortBy);

            const list = result.list;
            setOptions(list);
          } catch (e) {
            console.error('Failed to query caregiver data');
          } finally {
            setLoading(false);
          }
        }
      };
      fetchData();
    }
  }, [inputValue]);

  const handleInputChange = (event, newValue, reason) => {
    if (reason === 'input') {
      setFetchNewData(true);
    } else {
      setFetchNewData(false);
    }
    setInputValue(newValue); // Always update the inputValue for display
  };

  const handleOptionSelect = (event, selectedOption) => {
    if (selectedOption) {
      setSelectedCaregiver(selectedOption); // Pass only the id of the selected option to the parent
    }
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      getOptionLabel={(option) => option.caregiverName || ''}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps?.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const matches = match(option.caregiverName, inputValue || '');
        const parts = parse(option.caregiverName, matches);

        return (
          <li {...props} key={option.caregiverRn}>
            {parts.map((part, index) => (
              <span
                key={index}
                style={{fontWeight: part.highlight ? 700 : 400}}
              >
                {part.text}
              </span>
            ))}
          </li>
        );
      }}
    />

  );
};

CaregiverAutocomplete.propTypes = {
  label: PropTypes.string.isRequired,
  selectedCaregiver: PropTypes.object,
  setSelectedCaregiver: PropTypes.func.isRequired,
};

export default CaregiverAutocomplete;
